import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { marked } from 'marked'
import { data } from 'react-router'
import { type Route } from './+types/website-terms-of-use.ts'

const MetaTitle = 'Dealer Website Terms of Use | Dealer Pro Edge'
const MetaDescription =
	'Terms governing visitor use of dealership websites powered by Dealer Pro Edge.'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

export const meta: Route.MetaFunction = ({ matches }) => {
	const rootData = matches[0]?.data as
		| { requestInfo?: { origin: string; path: string } }
		| undefined
	const origin = rootData?.requestInfo?.origin ?? 'https://dealerproedge.com'
	const path = rootData?.requestInfo?.path ?? '/website-terms-of-use'
	const canonicalUrl = `${origin}${path}`

	return [
		{ title: MetaTitle },
		{ name: 'description', content: MetaDescription },
		{ name: 'robots', content: 'noindex, nofollow' },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
	]
}

// =============================================================================
// MARKDOWN CONTENT
// =============================================================================

const markdown = `# Terms and Conditions for Dealer Pro Edge

**Last Updated:** April 15, 2026

Please read these Terms and Conditions carefully before using Our Service.

## 1. Definitions
- **“Company,” “Dealer Pro Edge,” “we,” “us,” or “our”** means Dealer Pro Edge, LLC, 142 Swendsen Drive, Monroe, CT 06468, United States.
- **“Service”** means the Dealer Pro Edge website located at dealerproedge.com, together with any related platform access, software, subscriptions, services, features, content, communications, and functionality.
- **“You”** or **“Customer”** means the individual or entity accessing or using the Service.
- **“Account”** means a user account created to access parts of the Service.
- **“Content”** or **“Customer Data”** means text, images, files, data, feedback, comments, materials, or other information posted, uploaded, submitted, or otherwise made available through the Service.
- **“Subscriptions”** means recurring paid access to all or part of the Service.
- **“Goods”** means products, items, or deliverables (including digital goods) offered through the Service, if any.
- **“Orders”** means requests to purchase Goods, Services, or Subscriptions through the Service.
- **“Affiliate”** means an entity that controls, is controlled by, or is under common control with a party.

## 2. Acceptance of Terms
By accessing or using the Service, you agree to be bound by these Terms and Conditions. If you do not agree, do not use the Service.

You represent that you are at least 18 years old (or the age of majority in your jurisdiction) and legally able to enter into a binding agreement on behalf of yourself or the entity you represent. Your use of the Service is also subject to our Privacy Policy.

## 3. Use of the Service
You may use the Service only for lawful business purposes and in accordance with these Terms. You may not:
- use the Service in violation of any law or regulation;
- interfere with or disrupt the Service or related systems;
- attempt unauthorized access;
- use bots, scrapers, or automated means except with our prior written permission;
- upload malware, viruses, or harmful code;
- harass, defraud, or mislead others; or
- copy, reverse engineer, modify, or exploit the Service except as expressly permitted.

## 4. Accounts
If you create an Account, you must provide accurate, current, and complete information and keep it updated. You are responsible for maintaining the confidentiality of your login credentials and for all activity under your Account. Notify us immediately of any unauthorized use.

We may suspend or terminate an Account if it is inaccurate, compromised, inactive, or used in violation of these Terms.

## 5. Customer Cooperation
You agree to fully and timely cooperate with us in connection with the Service, including providing all information, data, and documentation reasonably requested by us.

## 6. Orders, Pricing, and Payments
If you place an Order, you represent that you are legally capable of entering binding contracts, the information you provide is true and complete, and you are authorized to use the payment method.

We reserve the right to refuse, limit, or cancel any Order. All purchases are subject to payment using approved methods. Payments may be processed by third-party processors. You authorize us and our processors to charge the applicable amounts. You are responsible for all taxes and charges.

Service Fees are non-cancelable and non-refundable except as expressly set forth herein. Overdue amounts may accrue interest at 1.5% per month (or the maximum rate permitted by law).

## 7. Subscription Terms
Subscriptions are billed in advance on a recurring basis according to the plan you select and automatically renew at the end of each billing cycle unless canceled.

**Cancellation**: You may cancel your subscription renewal through your Account settings (if available) or by contacting us. Cancellation takes effect at the end of the current billing period.

**Early Termination and Promotional Credits**: If you terminate a Subscription before the end of its term (other than for our material breach), you shall pay an early termination fee equal to two (2) months of the then-current subscription fee. If you received promotional credits or discounts and terminate during the promotional period or within thirty (30) days after such credits/discounts expire, you shall repay fifty percent (50%) of the total value of all credits or discounts received.

We may change subscription pricing upon reasonable prior notice; continued use after the change constitutes acceptance.

## 8. Free Trials and Promotions
We may offer free trials or promotions. You may need to provide billing information. Unless you cancel before the trial ends, you will be automatically charged the applicable subscription fee.

## 9. Returns and Cancellation for Digital Goods
For digital goods, software access, subscriptions, or completed services, refunds or cancellations are generally not available once access has been provided or the service has begun, except where required by law or at our sole discretion on a case-by-case basis.

## 10. User Content / Customer Data
As between the parties, you retain ownership of your Customer Data. You grant us a worldwide, non-exclusive, royalty-free, fully paid, sublicensable, and transferable license to host, store, reproduce, modify, display, transmit, distribute, and use your Customer Data as necessary to operate, provide, secure, improve, and promote the Service, and to create anonymized, aggregated statistical data (“Customer Statistics”) that we may use for any business purpose.

You represent and warrant that you own or have the necessary rights to your Customer Data and that it does not infringe third-party rights or violate these Terms.

We may remove Content that violates these Terms.

## 11. Content Restrictions
You may not submit or transmit Content that is unlawful, defamatory, abusive, obscene, fraudulent, contains malware, impersonates others, violates privacy rights, or is otherwise objectionable or prohibited by law.

## 12. Confidentiality
Each party agrees to maintain the confidentiality of the other party’s Confidential Information and to use it solely for the purpose of performing or receiving the Service. If either party is compelled by law to disclose Confidential Information, it shall provide the other party with prior notice (to the extent legally permitted) and reasonable assistance, at the other party’s cost, so that the other party may seek a protective order or other remedy.

## 13. Intellectual Property
The Service and all original content, features, and functionality (excluding your Customer Data) are the exclusive property of Dealer Pro Edge and its licensors. We grant you a limited, non-exclusive, non-transferable license to access and use the Service during the term of your Subscription solely for your internal business purposes.

We respect the intellectual property rights of others. If you believe that any material on the Service infringes your copyright or other intellectual property rights, please contact us at legal@dealerproedge.com with a description of the claimed infringement and its location. We will review and take appropriate action in our sole discretion, which may include removing the material.

You grant us the right to include the attribution “Powered by Dealer Pro Edge” (or a similar notice) on pages of the Service or outputs generated through the Service.

Any improvements, enhancements, suggestions, or derivative works developed by us (even if based on your feedback) are and shall remain our sole property.

## 14. Feedback
Any feedback or suggestions you provide is assigned to us (or you grant us a perpetual, royalty-free license to use it without restriction).

## 15. Third-Party Services and Links
We are not responsible for third-party websites, services, or their privacy practices.

## 16. Termination
Either party may terminate this Agreement or any Subscription for any reason upon thirty (30) days’ prior written notice. We may also suspend or terminate your access immediately if you violate these Terms or create risk to us or the Service.

Upon termination:
- All licenses granted to you cease immediately.
- You remain obligated to pay all fees accrued up to the effective date of termination (including any early termination fees or promotional credit repayment obligations).
- Provisions that by their nature should survive (including confidentiality, intellectual property, limitation of liability, indemnification, and governing law) shall continue in effect.

## 17. Force Majeure
Neither party shall be liable for any failure or delay in performance due to events beyond its reasonable control (including fire, flood, strike, war, governmental action, or internet failure).

## 18. Disclaimer of Warranties
THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTIES OF ANY KIND.

## 19. Limitation of Liability
To the fullest extent permitted by law, our total liability will not exceed the greater of the amount you paid us in the thirty (30) days before the claim or US $100.

We are not liable for indirect, consequential, or punitive damages.

## 20. Indemnification
You agree to indemnify and hold us harmless from claims arising from your use of the Service, your Customer Data, or your violation of these Terms.

## 21. Governing Law
These Terms are governed by the laws of the State of Connecticut, without regard to conflict of law principles.

## 22. Dispute Resolution
You agree to first contact us to attempt informal resolution of any dispute.

## 23. Entire Agreement; No Reliance
These Terms constitute the entire agreement between you and us regarding the Service and supersede all prior or contemporaneous communications. You acknowledge that you have not relied on any statement, representation, or warranty not expressly set forth in these Terms.

## 24. Severability and Waiver
Invalid provisions will be modified to the extent possible; remaining provisions stay in effect. Failure to enforce a right is not a waiver.

## 25. Changes to These Terms
We may update these Terms. Material changes will be posted with a revised date and, where appropriate, additional notice. Continued use after changes constitutes acceptance.

## 26. Contact Us
**Dealer Pro Edge, LLC**  
Attn: Legal Department  
142 Swendsen Drive  
Monroe, CT 06468  
United States  

Email: legal@dealerproedge.com (or support@dealerproedge.com for general inquiries)
`

// =============================================================================
// LOADER
// =============================================================================

export async function loader() {
	const html = await marked.parse(markdown)
	return data({ html })
}

// =============================================================================
// ROUTE COMPONENT
// =============================================================================

export default function DealerWebsiteTermsOfUseRoute({
	loaderData,
}: Route.ComponentProps) {
	return (
		<main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
			<article
				className="prose prose-neutral max-w-none [&_h2]:mb-2 [&_h2]:mt-5"
				dangerouslySetInnerHTML={{ __html: loaderData.html }}
			/>
		</main>
	)
}
